<script setup lang="ts">
import {
  ArrowLeft,
  CircleHelp,
  LogOut,
  MoreHorizontal,
  Settings,
  Building2,
} from 'lucide-vue-next';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { PLATFORM_APP_ICON_PATH, PLATFORM_APP_NAME } from '~/lib/platformBrand';

const authStore = useAuthStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();
const route = useRoute();
const { navGroups, settingsNavGroups, isItemActive } = useAppNavigation();
const { startWelcomeTour } = useWorkspaceTour();
const publicWorkspaceBranding = useState<Partial<import('~/types/models').Tenant> | null>('public-workspace-branding', () => null);
const isSettingsPath = (path: string) => path.includes('/settings') || path.includes('/account');
const navView = ref<'workspace' | 'settings'>(
  isSettingsPath(route.path)
    ? 'settings'
    : 'workspace',
);
const visibleNavGroups = computed(() => (
  navView.value === 'settings' ? settingsNavGroups.value : navGroups.value
));

const workspaceName = computed(() => (
  tenantStore.currentTenant?.name
  || publicWorkspaceBranding.value?.name
  || 'Workspace'
));
const workspaceLogoUrl = computed(() =>
  tenantStore.currentTenant?.tenantImageURL
  || publicWorkspaceBranding.value?.tenantImageURL
  || '',
);
const workspaceInitials = computed(() =>
  workspaceName.value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'V',
);

const handleLogout = async() => {
  await authStore.logout();
  await navigateTo('/auth/login', { replace: true });
}

const displayName = computed(() => authStore.user?.displayName || userStore.user?.displayName || 'User');
const email = computed(() => authStore.user?.email || userStore.user?.email || '');
const avatarURL = computed(() => authStore.user?.photoURL || userStore.user?.avatarURL || '');
const initials = computed(() => {
  const name = displayName.value;
  return name.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase();
});

function badgeClasses(badge?: string): string {
  if (!badge) return '';

  return 'mr-3 mt-1 inline-flex h-4.5 min-w-0 items-center rounded-full border bg-secondary px-1.5 text-[9px] font-medium uppercase tracking-[0.08em] text-secondary-foreground shadow-none';
}

function openSettingsNavigation() {
  navView.value = 'settings';
}

function openWorkspaceNavigation() {
  navView.value = 'workspace';
}

function navigationTourTarget(title: string): string | undefined {
  if (title === 'Clients') return 'nav-clients';
  return undefined;
}

watch(
  () => route.path,
  (path) => {
    navView.value = isSettingsPath(path)
      ? 'settings'
      : 'workspace';
  },
);

</script>

<template>
  <Sidebar data-tour="workspace-navigation" variant="floating" class="app-shell-sidebar border-r-0 bg-background [--sidebar:var(--card)] [--sidebar-accent:color-mix(in_srgb,var(--primary)_9%,var(--card))] [--sidebar-accent-foreground:var(--foreground)] [--sidebar-border:var(--border)]">
        <svg class="pointer-events-none absolute size-0" aria-hidden="true" focusable="false">
          <defs>
            <linearGradient id="vuey-nav-icon-gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stop-color="var(--primary)" />
              <stop offset="100%" stop-color="light-dark(var(--brand-accent), var(--brand-dark-accent))" />
            </linearGradient>
          </defs>
        </svg>
        <SidebarHeader class="px-3 pb-2 pt-4">
          <div class="mb-4 flex items-center gap-3 px-2 py-1">
            <img
              :src="PLATFORM_APP_ICON_PATH"
              alt="Vuey Studio icon"
              class="h-9 w-auto object-contain"
            />
            <div class="min-w-0">
              <p class="truncate text-sm font-semibold text-sidebar-foreground">{{ PLATFORM_APP_NAME }}</p>
            </div>
          </div>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                as="div"
                size="lg"
                class="cursor-default rounded-xl bg-muted/45 px-3.5 py-3 shadow-none" -->
              >
                <div class="relative flex aspect-square size-9 items-center justify-center overflow-hidden rounded-full border bg-background text-sidebar-foreground">
                  <img
                    v-if="workspaceLogoUrl"
                    :src="workspaceLogoUrl"
                    :alt="`${workspaceName} logo`"
                    class="h-full w-full object-cover"
                  />
                  <span v-else class="text-xs font-semibold tracking-[0.08em]">{{ workspaceInitials }}</span>
                </div>
                <div class="min-w-0 flex-1 text-left text-sm leading-tight">
                  <span class="block truncate text-sm font-medium text-sidebar-foreground">{{ workspaceName }}</span>
                </div>
                <Building2 class="size-4 shrink-0 text-sidebar-foreground/38" />
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent class="no-scrollbar px-3 pb-3 pt-1">
          <Transition
            mode="out-in"
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="translate-x-3 opacity-0"
            enter-to-class="translate-x-0 opacity-100"
            leave-active-class="transition duration-140 ease-in"
            leave-from-class="translate-x-0 opacity-100"
            leave-to-class="-translate-x-3 opacity-0"
          >
            <div :key="navView">
              <div v-if="navView === 'settings'" class="px-2 pb-2 pt-1">
                <button
                  type="button"
                  class="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
                  @click="openWorkspaceNavigation"
                >
                  <ArrowLeft class="size-4" />
                  Back to workspace
                </button>
                <p class="px-2 pt-3 text-base font-semibold text-sidebar-foreground">Settings</p>
              </div>

              <SidebarGroup
                v-for="group in visibleNavGroups"
                :key="group.id"
                :class="group.label ? 'pb-2 pt-4 first:pt-1' : 'pb-3 pt-1'"
              >
                <SidebarGroupLabel
                  v-if="group.label"
                  class="px-2.5 pb-1.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-sidebar-foreground/40"
                >
                  {{ group.label }}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem
                      v-for="item in group.items"
                      :key="`${group.id}-${item.title}`"
                      class="relative px-1 text-muted-foreground"
                    >
                      <SidebarMenuButton
                        v-if="item.url && !item.disabled"
                        as-child
                        :is-active="isItemActive(group.label || 'Workspace', item.url)"
                        class="nav-menu-button justify-start gap-2 text-sidebar-foreground"
                      >
                        <NuxtLink :to="item.url" :data-tour="navigationTourTarget(item.title)">
                          <component :is="item.icon" class="nav-menu-icon" />
                          <span>{{ item.title }}</span>
                        </NuxtLink>
                      </SidebarMenuButton>

                      <SidebarMenuButton
                        v-else
                        :disabled="item.disabled && !item.action"
                        class="nav-menu-button justify-start gap-2 text-sidebar-foreground"
                        @click="item.action?.()"
                      >
                        <component :is="item.icon" class="nav-menu-icon" />
                        <span>{{ item.title }}</span>
                      </SidebarMenuButton>

                      <SidebarMenuBadge
                        v-if="item.notificationCount || item.badge"
                        :class="badgeClasses(item.notificationCount ? String(item.notificationCount) : item.badge)"
                      >
                        {{ item.notificationCount || item.badge }}
                      </SidebarMenuBadge>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </div>
          </Transition>
        </SidebarContent>
        <SidebarFooter class="border-t border-sidebar-border/60 px-3 pb-4 pt-3">
          <button
            type="button"
            class="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
            @click="navView === 'settings' ? openWorkspaceNavigation() : openSettingsNavigation()"
          >
            <component :is="navView === 'settings' ? ArrowLeft : Settings" class="size-4" />
            <span>{{ navView === 'settings' ? 'Back to workspace' : 'Settings' }}</span>
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <button data-tour="workspace-account-menu" class="flex w-full items-center gap-3 rounded-xl bg-transparent px-3 py-3 text-left shadow-none transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Avatar class="h-8 w-8 rounded-md">
                  <AvatarImage :src="avatarURL || undefined" :alt="displayName" />
                  <AvatarFallback class="rounded-md bg-secondary text-xs font-semibold text-secondary-foreground">
                    {{ initials }}
                  </AvatarFallback>
                </Avatar>
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-medium leading-tight text-sidebar-foreground">{{ displayName }}</p>
                  <p class="truncate text-xs text-sidebar-foreground/58">{{ email }}</p>
                </div>
                <MoreHorizontal class="size-4 shrink-0 text-sidebar-foreground/45" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" class="w-56">
              <DropdownMenuLabel class="p-0 font-normal">
                <div class="flex items-center gap-2 px-2 py-1.5 text-left text-sm">
                  <Avatar class="h-8 w-8 rounded-lg">
                    <AvatarImage :src="avatarURL || undefined" :alt="displayName" />
                    <AvatarFallback class="rounded-lg bg-secondary text-xs font-semibold text-secondary-foreground">
                      {{ initials }}
                    </AvatarFallback>
                  </Avatar>
                  <div class="grid flex-1 text-left text-sm leading-tight">
                    <span class="truncate font-semibold">{{ displayName }}</span>
                    <span class="truncate text-xs text-muted-foreground">{{ email }}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem class="gap-2" @click="startWelcomeTour({ force: true })">
                <CircleHelp class="size-4" />
                Take workspace tour
              </DropdownMenuItem>
              <DropdownMenuItem class="gap-2" @click="openSettingsNavigation">
                <Settings class="size-4" />
                Open settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem class="gap-2 text-destructive focus:text-destructive focus:bg-destructive/10" @click="handleLogout">
                <LogOut class="size-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarFooter>
  </Sidebar>
</template>

<style scoped>
.nav-menu-icon {
  transition: stroke 180ms ease-out, filter 180ms ease-out, transform 180ms ease-out, opacity 180ms ease-out;
}

.nav-menu-button:hover .nav-menu-icon,
.nav-menu-button:focus-visible .nav-menu-icon,
.nav-menu-button[data-active='true'] .nav-menu-icon {
  stroke: url('#vuey-nav-icon-gradient');
  filter: drop-shadow(0 1px 3px color-mix(in srgb, var(--primary) 24%, transparent));
  opacity: 1;
  transform: scale(1.06);
}

@media (prefers-reduced-motion: reduce) {
  .nav-menu-icon {
    transition: none;
  }

  .nav-menu-button:hover .nav-menu-icon,
  .nav-menu-button:focus-visible .nav-menu-icon,
  .nav-menu-button[data-active='true'] .nav-menu-icon {
    transform: none;
  }
}
</style>
